import React from 'react';
import { WebUiInput } from 'src/modules/web-ui/components/input';
import { twMerge } from 'tailwind-merge';

import { WebUiButton } from '../../../components/button';
import { type RelaySubnet } from './types';

function ifaceName(vid: number): string {
  return vid === 0 ? 'Lan' : `VLAN_ID${vid}`;
}

function sortKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );
}

export default function DhcpRelaySubnetEditor({
  readonly,
  subnets,
  openedVid,
  setOpenedVid,
  setSubnets,
  onSaveSubnet,
}: {
  readonly: boolean;
  subnets: RelaySubnet[];
  openedVid: number | null;
  setOpenedVid: (vid: number) => void;
  setSubnets: React.Dispatch<React.SetStateAction<RelaySubnet[]>>;
  onSaveSubnet: (vid: number) => void;
}) {
  if (!subnets.length) {
    return <div className="text-sm">No relay subnet configuration found.</div>;
  }

  return (
    <div className="flex flex-col gap-0">
      <div className="mb-2 flex flex-wrap gap-2 p-1">
        {subnets.map((s) => (
          <WebUiButton
            key={s.vid}
            onClick={() => setOpenedVid(s.vid)}
            className={twMerge([
              'px-2 py-1 text-sm',
              openedVid === s.vid ? 'bg-webui-primary' : 'bg-transparent',
            ])}
          >
            {ifaceName(s.vid)}
          </WebUiButton>
        ))}
      </div>
      {subnets.map((s) =>
        openedVid === s.vid ? (
          <div key={s.vid} className="border border-webui-border p-2">
            <h6 className="text-sm font-bold">DHCP Server Settings</h6>
            <table className="webui-table w-full">
              <thead>
                <tr className="webui-table-row-head">
                  <td>Option</td>
                  <td>Setting</td>
                </tr>
              </thead>
              <tbody>
                <tr className="webui-table-row-content">
                  <td>Server IP Address</td>
                  <td>
                    <WebUiInput
                      inputSize="sm"
                      className="w-full"
                      disabled={readonly}
                      value={s.servers?.[0]?.address ?? ''}
                      onChange={(e) =>
                        setSubnets((prev) =>
                          prev.map((x) =>
                            x.vid !== s.vid
                              ? x
                              : {
                                  ...x,
                                  servers: [
                                    {
                                      address: e.target.value,
                                      port: x.servers?.[0]?.port ?? 67,
                                    },
                                  ],
                                },
                          ),
                        )
                      }
                    />
                  </td>
                </tr>
                <tr className="webui-table-row-content">
                  <td>Server Protocol Port (default 67)</td>
                  <td>
                    <WebUiInput
                      inputSize="sm"
                      className="w-full"
                      disabled={readonly}
                      type="number"
                      value={s.servers?.[0]?.port ?? 67}
                      onChange={(e) =>
                        setSubnets((prev) =>
                          prev.map((x) =>
                            x.vid !== s.vid
                              ? x
                              : {
                                  ...x,
                                  servers: [
                                    {
                                      address: x.servers?.[0]?.address ?? '',
                                      port: Number(e.target.value),
                                    },
                                  ],
                                },
                          ),
                        )
                      }
                    />
                  </td>
                </tr>
              </tbody>
            </table>
            <h6 className="mt-3 text-sm font-bold">Port Settings</h6>
            <table className="webui-table w-full">
              <thead>
                <tr className="webui-table-row-head">
                  <td>Option</td>
                  <td>Setting</td>
                </tr>
              </thead>
              <tbody>
                {sortKeys(Object.keys(s.ports ?? {})).map((key) => (
                  <tr
                    key={`${s.vid}-${key}`}
                    className="webui-table-row-content"
                  >
                    <td>{key} Circuit Port ID</td>
                    <td>
                      <WebUiInput
                        inputSize="sm"
                        className="w-full"
                        disabled={readonly}
                        value={s.ports?.[key] ?? ''}
                        onChange={(e) =>
                          setSubnets((prev) =>
                            prev.map((x) =>
                              x.vid !== s.vid
                                ? x
                                : {
                                    ...x,
                                    ports: {
                                      ...x.ports,
                                      [key]: e.target.value,
                                    },
                                  },
                            ),
                          )
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2">
              <WebUiButton
                disabled={readonly}
                onClick={() => onSaveSubnet(s.vid)}
              >
                Set Subnet VLAN ID {s.vid}
              </WebUiButton>
            </div>
          </div>
        ) : null,
      )}
    </div>
  );
}
